import React from 'react';
import { useAppConfig } from '../../context/AppConfigContext';
import { OperationalScreen } from './OperationalScreen';

interface ConfigUnavailableScreenProps {
  onRetry: () => void;
  retrying: boolean;
}

/**
 * Full-viewport screen rendered when the remote app-config never loaded
 * (configStatus === 'unavailable'). The funnel must NOT render with the
 * hardcoded DEFAULT_APP_CONFIG: it would show stale prices and possibly
 * discontinued recipes. Texts are static on purpose — without backend there
 * are no editable translations to read. The logo, however, IS platform-aware:
 * logoImage/platform se derivan del hostname/URL (detectPlatform), no del
 * backend, así que el branding dual (Patitas&co en PrestaShop) se respeta
 * incluso sin config.
 */
export const ConfigUnavailableScreen: React.FC<ConfigUnavailableScreenProps> = ({ onRetry, retrying }) => {
  const { logoImage, platform } = useAppConfig();
  return (
    <OperationalScreen
      logoSrc={logoImage}
      logoAlt={platform === 'prestashop' ? 'Patitas&co' : 'Milo y Lola'}
      emoji="🍲"
      title="Estamos actualizando nuestra carta"
      message="No podemos mostrarte las recetas y precios al día en este momento. Vuelve a intentarlo en unos minutos."
      action={
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="inline-block mt-4 px-6 py-3 rounded-xl font-fredoka text-base font-medium bg-[#008BBC] text-white hover:bg-[#007aa8] shadow-sm transition-all disabled:opacity-60 disabled:cursor-wait"
        >
          {retrying ? 'Comprobando…' : 'Reintentar'}
        </button>
      }
    />
  );
};

/**
 * Lightweight full-viewport loader for the initial config fetch
 * (configStatus === 'loading'). Shown for a few hundred ms at most.
 */
export const ConfigLoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-[#F7F4EE] flex flex-col items-center justify-center px-6">
    <div
      role="status"
      aria-label="Cargando"
      className="h-10 w-10 rounded-full border-4 border-[#008BBC]/20 border-t-[#008BBC] animate-spin"
    />
  </div>
);
